import type { ReactElement } from 'react';
import { Person } from 'react-bootstrap-icons';

import { DataTestIds } from '@repo/frontend-shared';
import type { SDSAvatarSize } from '@repo/sds/components/SDSAvatar';
import { SDSAvatar } from '@repo/sds/components/SDSAvatar';
import { SDSFlexbox } from '@repo/sds/components/SDSFlexbox';
import type { SDSTextType } from '@repo/sds/components/SDSText';
import { SDSText } from '@repo/sds/components/SDSText';
import { SDSTextBadge } from '@repo/sds/components/SDSTextBadge';

type SDSUserBadgeSize = 'medium' | 'large';

export interface SDSUserBadgeProps {
  name: string;
  avatarSrc: string | null | undefined;
  isLoading?: boolean;
  hideAvatar?: boolean;
  description?: string;
  size?: SDSUserBadgeSize;
  dataTestId?: string;
  BadgeWidth?: boolean;
  userRole?: string;
  showRoleBadge?: boolean;
  color?: 'text-primary' | 'text-secondary' | 'primary' | 'secondary' | 'alert' | 'info' | 'warning';

  background?: string;
  icon?: ReactElement;
}

const AVATAR_SIZES: { [key in SDSUserBadgeSize]: SDSAvatarSize } = {
  medium: 'small',
  large: 'big',
};

const NAME_TEXT_SIZES: { [key in SDSUserBadgeSize]: SDSTextType } = {
  medium: 'body-medium-bold',
  large: 'body-large-bold',
};

export function SDSUserBadge({
  name,
  avatarSrc,
  isLoading,
  hideAvatar = false,
  description,
  size = 'medium',
  dataTestId,
  BadgeWidth,
  showRoleBadge,
  userRole,
  color,
  background,
  icon,
}: SDSUserBadgeProps) {
  return (
    <SDSFlexbox
      dataTestId={dataTestId ?? DataTestIds.SDS.SDS_USER_BADGE}
      alignItems="center"
      gap="text-medium"
      {...(!BadgeWidth && { fullWidth: true })}
    >
      {!hideAvatar && (
        <SDSAvatar
          src={avatarSrc}
          alt="Avatar of the user"
          name={name}
          size={AVATAR_SIZES[size]}
          isLoading={isLoading}
        />
      )}
      <SDSFlexbox direction="column" gap="text-small" alignItems="flex-start" fullWidth overflowHidden>
        <SDSFlexbox gap="text-medium" alignItems="center">
          <SDSText type={NAME_TEXT_SIZES[size]} text={name} isLoading={isLoading} />
          {showRoleBadge && userRole && (
            <SDSFlexbox>
              <SDSTextBadge
                text={userRole}
                icon={icon ?? <Person />}
                size={'x-small' as const}
                color={color ?? 'text-primary'}
                background={background}
                type="tag"
              />
            </SDSFlexbox>
          )}
        </SDSFlexbox>
        {description && (
          <SDSText type="body-small" text={description} color="muted" isLoading={isLoading} maxRows={1} />
        )}
      </SDSFlexbox>
    </SDSFlexbox>
  );
}
